import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { CustomFormContext } from "../../CustomForm";
import { prepareContent } from "../../helpers";
import { ErrorInfo, Validation, Option } from "../../types";
import { useTranslation } from "react-i18next";
import { FieldTypeId } from "../../../../lib/enums";
import React from "react";

interface CustomRadioGroupProps<T extends Option> {
  validations?: Validation[];
  fieldId: number;
  alias: string;
  header: string;
  typeId: FieldTypeId;
  fieldOptions: readonly T[] | readonly string[] | readonly number[];
  localized?: boolean;
  valueProp?: string;
  labelProp?: string;
  row?: boolean;
  readOnly?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

const CustomRadioGroup = <T extends Option>({
  fieldId,
  alias,
  header,
  typeId,
  fieldOptions,
  valueProp = undefined,
  labelProp = undefined,
  localized = false,
  row = false,
  readOnly = false,
  required = false,
  placeholder = "",
  className = "",
  style = {},
}: CustomRadioGroupProps<T>) => {
  const {
    fieldValue,
    setDataDispatch,
    dataIsSet,
    errorsAreSet,
    getValidationErrors,
    readOnlyState,
    tempData,
    showErrors,
  } = useContext(CustomFormContext);
  const { t } = useTranslation();
  const [values, setValues] = useState<T | null>(null);
  const [errors, setErrors] = useState<ErrorInfo[] | null>(null);

  useEffect(() => {
    if (!dataIsSet || !errorsAreSet) {
      return;
    }
    const data = fieldValue(alias);
    const errors = getValidationErrors(alias);
    if (data === undefined) {
      return;
    }
    setValues(data);
    if (errors) {
      setErrors(errors);
    }
  }, [dataIsSet, errorsAreSet, tempData]);

  const getLabel = (option: T | string | number) => {
    if (labelProp && option.hasOwnProperty(labelProp)) {
      return (option as any)[labelProp];
    } else if (valueProp && option.hasOwnProperty(valueProp)) {
      return (option as any)[valueProp];
    }
    return option;
  };

  const handleOnChange = (option: T) => {
    setValues(() => {
      if (valueProp && option.hasOwnProperty(valueProp)) {
        return (option as any)[valueProp];
      }
      return option;
    });
  };

  const handleSave = () => {
    const data = prepareContent(values, alias);
    setDataDispatch({
      type: "UPDATE_CONTENT",
      payload: data,
    });
  };

  useEffect(() => {
    handleSave();
  }, [values]);

  useEffect(() => {
    setErrors(getValidationErrors(alias));
  }, [getValidationErrors, alias]);

  return (
    <>
      <FormControl>
        <FormLabel id={`${alias}-label`}>
          {header}{" "}
          {(required || errors?.find((a) => a.error === "isNotNullOrEmpty")) &&
            "*"}{" "}
        </FormLabel>
        {showErrors &&
          errors?.map((info, index) => (
            <FormHelperText key={index} variant="standard">
              <Typography variant="caption" color="red">
                {info.message}
              </Typography>
            </FormHelperText>
          ))}
        <RadioGroup
          aria-labelledby={`${alias}-label`}
          value={values}
          row={row}
          onChange={(e) => {
            e.preventDefault();
            handleOnChange(e.target.value as unknown as T);
          }}
        >
          {fieldOptions &&
            fieldOptions.map((option) => {
              const value =
                valueProp && option.hasOwnProperty(valueProp)
                  ? (option as any)[valueProp]
                  : option;

              return (
                <FormControlLabel
                  key={`${alias}-option-${value}`}
                  value={value}
                  control={<Radio readOnly={readOnly || readOnlyState} />}
                  label={getLabel(option)}
                />
              );
            })}
        </RadioGroup>
      </FormControl>
    </>
  );
};

CustomRadioGroup.fieldType = "CustomRadioGroup";

export default CustomRadioGroup;
